import React, { useEffect, useState, useContext, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  IonGrid,
  IonRow,
  IonCol,
  IonSpinner,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import getSportsFeed from '../../global/request/sports';
import { AuthContext } from '../authentication/AuthContext';
import TrendingCard from './TrendingCard';
import comingSoonImage from './comingsoon.png';
import '../../styles/home/Trending.scss';

const Trending = () => {
  const [trendingData, setTrendingData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [selectedLeague, setSelectedLeague] = useState('');
  const { tokens } = useContext(AuthContext);

  const leagueOptions = [
    { value: '', label: 'All Sports' },
    { value: 'NBA', label: 'NBA - National Basketball Association' },
    { value: 'NFL', label: 'NFL - National Football League' },
    { value: 'MLB', label: 'MLB - Major League Baseball' },
    { value: 'CFB', label: 'CFB - College Football' },
    { value: 'ATP', label: 'ATP - Association of Tennis Professionals' },
    { value: 'MLS', label: 'MLS - Major League Soccer' },
    { value: 'CBB', label: 'CBB - College Basketball' },
    { value: 'USL', label: 'USL - United Soccer League' },
    { value: 'WNBA', label: "WNBA - Women's National Basketball Association" },
    { value: 'NCAAM', label: "NCAAM - Men's College Basketball" },
    { value: 'PGA', label: 'PGA - Professional Golf Association' },
    { value: 'EPL', label: 'EPL - English Premier League' },
  ];

  const fetchTrendingData = useCallback(
    async (token = null) => {
      try {
        const params = {
          league: selectedLeague,
          source_type: 'trending',
          limit: 10,
        };

        if (token) {
          params.next_token = token;
        }

        const response = await getSportsFeed(tokens.idToken, params);

        if (response.body && response.body.data) {
          const dataWithIds = response.body.data.map(item => ({
            ...item,
            uniqueId: uuidv4(),
          }));

          const sortedData = dataWithIds.sort(
            (a, b) => parseInt(b.published_timestamp, 10) - parseInt(a.published_timestamp, 10),
          );

          if (token) {
            setTrendingData(prev => {
              const existingIds = new Set(prev.map(item => item.tweet_id));
              const newItems = sortedData.filter(item => !existingIds.has(item.tweet_id));

              if (newItems.length === 0) {
                setNextToken(null);
                return prev;
              }

              const combined = [...prev, ...newItems];
              return combined.sort((a, b) => parseInt(b.published_timestamp, 10) - parseInt(a.published_timestamp, 10));
            });
          } else {
            setTrendingData(sortedData);
          }

          if (sortedData.length > 0) {
            setNextToken(response.body.next_token);
          } else {
            setNextToken(null);
          }
          setError(null);
        } else {
          console.error('Invalid response format:', response);
          setError('Invalid response format from server');
        }
      } catch (err) {
        console.error('Error details:', err);
        setError('Failed to load trending content. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    },
    [tokens?.idToken, selectedLeague],
  );

  useEffect(() => {
    if (tokens?.idToken) {
      setTrendingData([]);
      setNextToken(null);
      setIsLoading(true);
      fetchTrendingData();
    }
  }, [fetchTrendingData, tokens?.idToken]);

  const handleLeagueChange = event => {
    setSelectedLeague(event.detail.value);
  };

  const loadMore = async e => {
    if (nextToken) {
      await fetchTrendingData(nextToken);
    }
    e.target.complete();
  };

  if (isLoading) {
    return (
      <div className='trending-loading'>
        <IonSpinner name='crescent' />
        <p>Loading trending content...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className='trending-error'>
        <img
          src={comingSoonImage}
          alt='Coming Soon'
          style={{
            width: '100%',
            maxWidth: '400px',
            height: 'auto',
            display: 'block',
            margin: '0 auto',
          }}
        />
      </div>
    );
  }

  return (
    <>
      <div className='trending-filter'>
        <IonSelect
          value={selectedLeague}
          placeholder='Select Sport'
          className='entry-field-select'
          onIonChange={handleLeagueChange}
          interface='popover'
          style={{
            '--width': '90vw',
            '--max-width': '400px',
          }}>
          {leagueOptions.map(option => (
            <IonSelectOption key={option.value} value={option.value}>
              {option.label}
            </IonSelectOption>
          ))}
        </IonSelect>
      </div>

      {isLoading && (
        <div className='trending-loading'>
          <IonSpinner name='crescent' />
        </div>
      )}

      {error && (
        <div className='trending-error'>
          <p>{error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <>
          <IonGrid>
            <IonRow>
              {trendingData.map(item => (
                <IonCol size='12' sizeMd='6' sizeLg='4' key={item.uniqueId}>
                  <TrendingCard item={item} />
                </IonCol>
              ))}
            </IonRow>
          </IonGrid>
          <IonInfiniteScroll onIonInfinite={loadMore} disabled={!nextToken}>
            <IonInfiniteScrollContent loadingText={nextToken ? 'Loading more content...' : 'No more content to load'}>
              <IonSpinner name='crescent' />
            </IonInfiniteScrollContent>
          </IonInfiniteScroll>
        </>
      )}
    </>
  );
};

export default Trending;
